import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import TravelDetailsForm from "./TravelDetailsForm";

interface RemoteWorkRequest {
  id: string;
  destination_country: string;
  start_date: string;
  end_date: string;
  requested_points: number;
  status: string;
  comment: string | null;
  created_at: string;
  residence_departure_date?: string;
  destination_arrival_date?: string;
  residence_address_destination?: string;
  insurance_active?: boolean;
  foreign_number?: string;
  vpn_active?: boolean;
  vpn_code?: string;
}

interface TripsTabProps {
  employeeId: string | null;
}

const TripsTab = ({ employeeId }: TripsTabProps) => {
  const [requests, setRequests] = useState<RemoteWorkRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);

  useEffect(() => {
    if (employeeId) {
      fetchRequests();
    }
  }, [employeeId]);

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('remote_work_requests')
        .select('*')
        .eq('employee_id', employeeId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error: any) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestUpdate = (updatedRequest: RemoteWorkRequest) => {
    setRequests(prevRequests => 
      prevRequests.map(req => 
        req.id === updatedRequest.id ? updatedRequest : req
      )
    );
  };

  const toggleExpanded = (requestId: string) => {
    setExpandedRequest(expandedRequest === requestId ? null : requestId);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">✓ Approuvée</Badge>;
      case 'rejected':
        return <Badge variant="destructive">✗ Refusée</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'border-green-200 bg-green-50';
      case 'rejected':
        return 'border-red-200 bg-red-50';
      case 'pending':
      default:
        return 'border-yellow-200 bg-yellow-50';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-2 text-muted-foreground">Chargement de vos voyages...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Mes demandes de télétravail
          </CardTitle>
          <CardDescription>
            Historique complet de toutes vos demandes de workation
          </CardDescription>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Aucun voyage pour le moment</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Vous n'avez encore fait aucune demande de télétravail. Commencez par explorer nos destinations tendances et faites votre première demande !
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div key={request.id} className={`p-6 border rounded-lg transition-all hover:shadow-md ${getStatusColor(request.status)}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <MapPin className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-semibold text-foreground">
                          {request.destination_country}
                        </h3>
                        {getStatusBadge(request.status)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-muted-foreground">Période:</span>
                          <p className="text-foreground mt-1">
                            Du {new Date(request.start_date).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-foreground">
                            Au {new Date(request.end_date).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                        
                        <div>
                          <span className="font-medium text-muted-foreground">Points utilisés:</span>
                          <p className="text-foreground mt-1 flex items-center">
                            <span className="text-lg font-semibold">{request.requested_points}</span>
                            <span className="ml-1">🪺</span>
                          </p>
                        </div>
                        
                        <div>
                          <span className="font-medium text-muted-foreground">Demandé le:</span>
                          <p className="text-foreground mt-1">
                            {new Date(request.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>

                      {request.comment && (
                        <div className="mt-4 p-3 bg-muted/50 rounded-md">
                          <div className="flex items-center gap-2 mb-1">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium text-sm text-muted-foreground">Commentaire:</span>
                          </div>
                          <p className="text-sm text-foreground">{request.comment}</p>
                        </div>
                      )}
                    </div>

                    {/* Travel Details Toggle for Approved Requests */}
                    {request.status === 'approved' && (
                      <div className="mt-4 pt-4 border-t">
                        <Button
                          variant="ghost"
                          onClick={() => toggleExpanded(request.id)}
                          className="w-full justify-between"
                        >
                          <span>Détails de voyage</span>
                          {expandedRequest === request.id ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                        
                        {expandedRequest === request.id && (
                          <TravelDetailsForm
                            request={request}
                            onUpdate={handleRequestUpdate}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trip Statistics */}
      {requests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Statistiques de voyage</CardTitle>
            <CardDescription>Résumé de votre activité de télétravail</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{requests.length}</div>
                <div className="text-sm text-muted-foreground">Demandes totales</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {requests.filter(r => r.status === 'approved').length}
                </div>
                <div className="text-sm text-muted-foreground">Approuvées</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {requests.filter(r => r.status === 'pending').length}
                </div>
                <div className="text-sm text-muted-foreground">En attente</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {requests.reduce((sum, r) => sum + (r.status === 'approved' ? r.requested_points : 0), 0)} 🪺
                </div>
                <div className="text-sm text-muted-foreground">Points utilisés</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TripsTab;